import { Error, Label } from "@tql/ui-components-react/kendo/labels";
import { Button } from "@tql/ui-components-react/tql/buttons";
import { TextBox } from "@tql/ui-components-react/tql/inputs/beta";

import { useAddressBook } from "~/features/address-book/hooks/use-address-book.ts";
import { LocationTypeOptions } from "~/features/quote/components/quote-details/components/pickup-delivery-details/components/location-type-options/location-type-options.tsx";
import { PostalCodeSearchRow } from "~/features/quote/components/quote-details/components/pickup-delivery-details/components/postal-code-search-row/postal-code-search-row.tsx";
import { ServiceOptions } from "~/features/quote/components/quote-details/components/pickup-delivery-details/components/service-options/service-options.tsx";
import { useAddressPostalCodeSelection } from "~/features/quote/components/quote-details/components/pickup-delivery-details/hooks/use-address-postal-code-selection.ts";
import { usePickupDeliveryDetails } from "~/features/quote/components/quote-details/components/pickup-delivery-details/hooks/use-pickup-delivery-details.ts";
import { blankAddress } from "~/features/quote/components/quote-details/constants/quote-detail-constants.ts";
import { ContactRow } from "~/features/quote/components/shipment-details/components/shipment-pickup-details/components/contact-row.tsx";
import { HoursAndNumberRow } from "~/features/quote/components/shipment-details/components/shipment-pickup-details/components/hours-and-number-row.tsx";
import { NameAndDateRow } from "~/features/quote/components/shipment-details/components/shipment-pickup-details/components/name-and-date-row.tsx";
import { getAddressTypeLabel } from "~/features/quote/components/shipment-details/components/shipment-pickup-details/utils/address-helpers.ts";
import { useQuoteAddressContext } from "~/features/quote/context/quote-address-context.tsx";
import { useApplyAddressBookLocation } from "~/features/quote/hooks/use-apply-address-book-location.ts";
import { useQuoteDetailsPageLoadQueries } from "~/features/quote/hooks/use-shipment-details-hooks.ts";
import { getAppliedAddressLabel } from "~/features/quote/utils/applied-address.ts";
import { buildAddressBookLocationFilter } from "~/features/quote/utils/build-address-book-location-filter.ts";
import { AddressBookModal } from "~/shared/components/address-book-modal/address-book-modal.tsx";
import type { SearchResult } from "~/shared/components/fields/address-postal-code-search.tsx";
import { AddressPostalCodeSearch } from "~/shared/components/fields/address-postal-code-search.tsx";
import { TraxModal } from "~/shared/components/trax-modal/trax-modal.tsx";
import { useAutoSpinner } from "~/shared/hooks/use-global-spinner.ts";
import { useIsMobile } from "~/shared/hooks/use-is-mobile.ts";
import { useNotificationActions } from "~/shared/hooks/use-notifications.ts";
import type { AddressBookCreateBodyV2 } from "~/shared/schemas/address-book-schema.ts";
import type {
	QuoteStateType,
	SubGroupKey,
} from "~/shared/types/quote-details-types";
import { hasContactDetailData } from "~/shared/utils/address-book-detail.ts";

import tenderLtlStyles from "../../index.module.css";
import { TenderAddressLines } from "./tender-address-lines.tsx";
import styles from "./tender-pickup-delivery-details.module.css";

type TenderPickupDeliveryDetailsProps = {
	isRepairMode?: boolean;
	type: SubGroupKey;
	hasFieldError: (field: string) => boolean;
	hideAddressBook?: boolean;
	onClearFieldError: (field: string) => void;
};

export const TenderPickupDeliveryDetails = ({
	isRepairMode = false,
	type,
	hasFieldError,
	hideAddressBook = false,
	onClearFieldError,
}: TenderPickupDeliveryDetailsProps) => {
	const { data: pageData } = useQuoteDetailsPageLoadQueries();
	const isMobile = useIsMobile();
	const pickupDeliveryDetails = usePickupDeliveryDetails(type);
	const {
		applyAddressBookAccessorials,
		handleCombinedSelectionChange,
		handleRadioSelectionChange,
		isOptionSelected,
	} = pickupDeliveryDetails;
	const { quoteState, onChange } = pickupDeliveryDetails;
	const {
		handleAddressPostalCodeChange,
		onToggleAddressBookModal,
		stopType,
		showAddressBookModal,
	} = useAddressPostalCodeSelection({ name: type, ...pickupDeliveryDetails });

	const { selectedAddressBookItems, updateSelectedAddressBookItem } =
		useQuoteAddressContext();
	const addressBookItem = selectedAddressBookItems[type];
	const { applyLocation, clearInvalidCountryAddress, invalidCountryAddress } =
		useApplyAddressBookLocation(type, pickupDeliveryDetails.countries);

	// The Saved Address search shows the applied address as a label derived from
	// the invariant: it appears only while the stop is a pristine saved address and
	// blanks the moment an identity field is edited (the context clears
	// addressBookItemId). This is correct in-session and on reload with no
	// remembered component state. clearOnSelect stays enabled on PostalCodeSearchRow
	// so typing a new query does not wipe the applied address.
	const savedAddressSearchValue = getAppliedAddressLabel(addressBookItem);

	const { createAddressBookItem, isCreating } = useAddressBook();
	const { showError } = useNotificationActions();

	useAutoSpinner(isCreating);

	const handleSaveAddressClick = () => {
		if (hideAddressBook || !addressBookItem) return;

		const isPickup = type === "pickup";
		const location = isPickup
			? quoteState.pickupPostalCode
			: quoteState.deliveryPostalCode;

		const body: AddressBookCreateBodyV2 = {
			postalCode: addressBookItem.postalCode || location.postalCode || "",
			country:
				addressBookItem.country || location.city?.state?.countryCode || "",
			city: addressBookItem.city || location.city?.name || "",
			customerId: addressBookItem.customerId || quoteState.customer.id,
			createdByCustomerId:
				addressBookItem.createdByCustomerId ?? quoteState.customer.id,
			address1: addressBookItem.address1,
			address2: addressBookItem.address2 || undefined,
			companyName: addressBookItem.companyName || "",
			stateProvince:
				addressBookItem.stateProvince ||
				location.city?.state?.abbreviation ||
				"",
			details:
				addressBookItem.details
					?.filter((detail) => {
						const isCurrentType =
							(type === "pickup" && detail.stopType === 1) ||
							(type === "delivery" && detail.stopType === 2);

						if (isCurrentType) return true;

						return hasContactDetailData(detail);
					})
					.map((detail) => ({
						...detail,
						contactName: detail.contactName ?? undefined,
						contactPhone: detail.contactPhone ?? undefined,
						hoursOpen: detail.hoursOpen ?? undefined,
						hoursClose: detail.hoursClose ?? undefined,
						timeZone: detail.timeZone ?? undefined,
						specialInstructions: detail.specialInstructions ?? "",
						accessorials: detail.accessorials ?? undefined,
					})) ?? [],
		};

		const requiredFields: readonly [string, string][] = [
			[`${getAddressTypeLabel(type)} Name`, body.companyName],
			["Address Line 1", body.address1],
			["Postal Code", body.postalCode],
			["City", body.city],
			["State", body.stateProvince],
			["Country", body.country],
		];
		const missingFields = requiredFields
			.filter(([, value]) => !value?.trim())
			.map(([label]) => label);

		if (missingFields.length > 0) {
			showError(
				`Cannot save address. Please provide: ${missingFields.join(", ")}.`,
			);
			return;
		}

		createAddressBookItem(body);
	};

	const handlePostalCodeSelectionChange = (result: SearchResult | null) => {
		const zipCode = result?.type === "postalCode" ? result.data : null;
		onClearFieldError(`${type}PostalCode`);
		updateSelectedAddressBookItem(
			type,
			"postalCode",
			zipCode?.postalCode ?? "",
		);
		updateSelectedAddressBookItem(type, "city", zipCode?.city?.name ?? "");
		updateSelectedAddressBookItem(
			type,
			"stateProvince",
			zipCode?.city?.state?.abbreviation ?? "",
		);
		updateSelectedAddressBookItem(
			type,
			"country",
			zipCode?.city?.state?.countryCode ?? "",
		);
		// Keep quoteState's postal code in sync so consumers that read from it
		// (e.g. the address book modal's "Filtered by Postal Code" banner) don't
		// show stale data after the field is changed or cleared.
		onChange(
			`${type}PostalCode` as keyof QuoteStateType,
			zipCode ?? blankAddress,
		);
	};

	const showAddressBookButton = isMobile && !hideAddressBook && !isRepairMode;

	return (
		<div
			className={`${styles.tenderPickupDeliveryDetailsWrapper} ${tenderLtlStyles.tenderLtlCard}`}
		>
			<div className={styles.contactDetails}>
				<div className={styles.contactDetailsHeader}>
					<div>
						<h4>{type == "pickup" ? "Pickup" : "Delivery"}</h4>
					</div>

					{hideAddressBook || isMobile || isRepairMode ? null : (
						<div className={styles.headerAddressSearch}>
							<PostalCodeSearchRow
								addressBookDisabled={
									!quoteState.customer || quoteState.customer.id == 0
								}
								clearOnSelect={true}
								customerId={quoteState.customer?.id}
								disabled={!quoteState.customer?.id}
								onAddressBookClick={onToggleAddressBookModal}
								onSelectionChange={handleAddressPostalCodeChange}
								onlySearchAddressBook={true}
								placeholder={"Saved Address"}
								stopType={stopType}
								value={savedAddressSearchValue}
							/>
						</div>
					)}
				</div>
				<NameAndDateRow
					hasFieldError={hasFieldError}
					holidays={pageData?.holidays}
					onClearFieldError={onClearFieldError}
					shipmentDateReadOnly={isRepairMode}
					type={type}
				/>
				<TenderAddressLines
					addressBookDisabled={
						!quoteState.customer || quoteState.customer.id == 0
					}
					hasFieldError={hasFieldError}
					onAddressBookClick={onToggleAddressBookModal}
					onClearFieldError={onClearFieldError}
					showAddressBookButton={showAddressBookButton}
					type={type}
				/>
				<div className={styles.locationRow}>
					<div className={styles.postalCodeField}>
						<Label editorId={`${type}PostalCode`}>
							Postal Code <span className={"required"}>*</span>
						</Label>
						<AddressPostalCodeSearch
							disabled={isRepairMode}
							id={`${type}PostalCode`}
							onSelectionChange={handlePostalCodeSelectionChange}
							onlySearchPostalCodes={true}
							showFullAddressOnSelect={false}
							showIcons={false}
							valid={!hasFieldError(`${type}PostalCode`)}
							value={addressBookItem?.postalCode ?? ""}
						/>
						{hasFieldError(`${type}PostalCode`) ? (
							<Error>Required</Error>
						) : null}
					</div>
					<div className={styles.locationCityField}>
						<Label editorId={`${type}City`}>City</Label>
						<TextBox
							disabled={true}
							id={`${type}City`}
							value={addressBookItem?.city ?? ""}
						/>
					</div>
					<div className={styles.locationStateField}>
						<Label editorId={`${type}State`}>State</Label>
						<TextBox
							disabled={true}
							id={`${type}State`}
							value={addressBookItem?.stateProvince ?? ""}
						/>
					</div>
					<div className={styles.locationCountryField}>
						<Label editorId={`${type}Country`}>Country</Label>
						<TextBox
							disabled={true}
							id={`${type}Country`}
							value={addressBookItem?.country ?? ""}
						/>
					</div>
				</div>
				<ContactRow
					hasFieldError={hasFieldError}
					onClearFieldError={onClearFieldError}
					type={type}
				/>
				<HoursAndNumberRow
					hasFieldError={hasFieldError}
					onClearFieldError={onClearFieldError}
					type={type}
				/>
			</div>

			<fieldset
				disabled={isRepairMode}
				style={{ border: 0, margin: 0, padding: 0, minWidth: 0 }}
			>
				<div className={styles.accessorialsTitle}>
					{type == "pickup" ? "Pickup" : "Delivery"} Accessorials Selection
				</div>
				<LocationTypeOptions
					disabled={isRepairMode}
					handleRadioSelectionChange={(option) => {
						if (!isRepairMode) handleRadioSelectionChange(option);
					}}
					isOptionSelected={isOptionSelected}
					name={type}
				/>

				<ServiceOptions
					disabled={isRepairMode}
					handleCombinedSelectionChange={(option, checked) => {
						if (!isRepairMode) handleCombinedSelectionChange(option, checked);
					}}
					isOptionSelected={isOptionSelected}
					name={type}
				/>

				{hideAddressBook || isRepairMode ? null : (
					<div className={styles.saveAddress}>
						<Button disabled={isCreating} onClick={handleSaveAddressClick}>
							{isCreating ? "Saving Address..." : "Save Address"}
						</Button>
					</div>
				)}
			</fieldset>
			{showAddressBookModal && !hideAddressBook && !isRepairMode ? (
				<AddressBookModal
					customerId={quoteState.customer?.id ?? 0}
					locationFilter={buildAddressBookLocationFilter(
						quoteState,
						type,
						addressBookItem,
					)}
					name={type}
					onClose={onToggleAddressBookModal}
					onSelect={(address) => {
						if (!applyLocation(address)) return;
						applyAddressBookAccessorials(address);
					}}
					useLocationFilter={true}
				/>
			) : null}
			{invalidCountryAddress ? (
				<TraxModal
					cancelText={"Close"}
					onCancel={clearInvalidCountryAddress}
					onClose={clearInvalidCountryAddress}
					showConfirmButton={false}
					showConfirmationButtons={true}
					title={"Invalid Country Code"}
					type={"error"}
				>
					<p>
						This address book item has a country code of &quot;
						{invalidCountryAddress.country}&quot; that cannot be recognized as a
						valid country. Please contact TQL Tech Support at{" "}
						<strong>(800) 580-3101 ext. 56400</strong> to have this record
						corrected.
					</p>
				</TraxModal>
			) : null}
		</div>
	);
};
